import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useTheme, Button, DataGrid, GridItem, GridRow, Icon, MenuItem, Modal, Spacer, TextField, Typography, Select, Tooltip, Link, SearchField } from '@emburse/embark-core';
import { ArrowForwardIcon, FileIcon, FolderIcon, SyncIcon, WarningIcon } from '@emburse/embark-icons';
import { styled } from '@mui/material';
import { FileUploadButton, IconMenu } from '../../../components';
import { downloadSFTPFile, moveSFTPFile, renameSFTPFile, uploadSFTPFile } from '../../../api/dataInterchange';
import { getCompanyPreference } from '../../../api/api';
import { createLogEvent } from '../../../api/errorViewer';
import { formatDate, getTimezone } from '../../../util/dateAdapter';
import { $t, useTranslation } from '../../../hooks/useTranslation';
const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
});
const downloadBase64File = (fileName, contentBase64) => {
    const linkSource = `data:application/pdf;base64,${contentBase64}`;
    const downloadLink = document.createElement('a');
    document.body.appendChild(downloadLink);
    downloadLink.href = linkSource;
    downloadLink.target = '_self';
    downloadLink.download = fileName;
    downloadLink.click();
};
const ModalFooter = styled(GridRow)({
    flex: 1,
    padding: '12px 24px',
    fontSize: 16,
    fontStyle: 'normal',
    overflowY: 'auto',
    fontFamily: 'Lato',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.15px',
    justifyContent: 'flex-end',
    columnGap: '8px'
});
function FileTable({ loading, data, updateData }) {
    useTranslation();
    const theme = useTheme();
    const [filterLoading, setFilterLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const [path, setPath] = useState('');
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [newPath, setNewPath] = useState('');
    const [newFilename, setNewFilename] = useState('');
    const [inArchive, setinArchive] = useState(false);
    const [hasSFTPDownloadLog, setHasSFTPDownloadLog] = useState(false);
    useEffect(() => {
        let tempData;
        if (path.length) {
            tempData = data.filter((folder) => folder.Path === path)[0].Files;
        }
        else {
            tempData = data;
        }
        if (searchFilter.length) {
            tempData = tempData.filter((value) => value.Name.toLowerCase().includes(searchFilter.toLowerCase()));
        }
        setFilteredData(tempData);
    }, [searchFilter, path, data]);
    useEffect(() => {
        setFilterLoading(false);
    }, [filteredData]);
    useEffect(() => {
        getCompanyPreference('SFTPDownloadLog', '').then((resp) => {
            setHasSFTPDownloadLog(resp.Data === '1');
        });
    }, []);
    const searchChangeHandler = useCallback((value) => {
        if (!filterLoading) {
            setFilterLoading(true);
        }
        setSearchFilter(value);
    }, [filterLoading]);
    const handleRowClick = (event) => {
        if (path === '' && event.row.Valid) {
            setPath(event.row.Path);
            setinArchive(event.row.Archive);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 30 }), _jsxs(GridRow, { children: [_jsx(SearchField, { label: $t('Search'), id: "activity-search-input", disabled: loading, doSearchOnChange: true, onChangeTimeout: 250, searchAction: searchChangeHandler }), _jsx("div", { style: { marginLeft: 'auto', columnGap: 8, display: 'flex' }, children: path.length !== 0 && !inArchive && (_jsx(GridItem, { children: _jsx(FileUploadButton, { multiple: true, onFileSelect: async (event) => {
                                    const file = event.target.files[0];
                                    const fileData = (await toBase64(file));
                                    uploadSFTPFile(file.name, path, fileData).then(() => updateData());
                                }, children: $t('Upload File') }) })) })] }), _jsx(Spacer, { size: 20 }), _jsx(Button, { endIcon: _jsx(Icon, { iconUrl: SyncIcon }), size: "small", variant: "outlined", onClick: () => {
                    if (!loading) {
                        updateData();
                    }
                }, children: $t('Refresh') }), _jsx(Spacer, { size: 30 }), _jsxs(GridRow, { style: { columnGap: 8 }, children: [_jsx(Button, { onClick: () => setPath(''), style: { color: theme.palette.text.secondary }, variant: "text", children: $t('Folders') }), path.length !== 0 && (_jsx(Icon, { style: { margin: '6px 8px', color: theme.palette.text.secondary }, iconUrl: ArrowForwardIcon })), path.length !== 0 && (_jsx(Typography, { style: { margin: '6px 8px', fontWeight: 700 }, children: path }))] }), _jsx(DataGrid, { loading: loading || filterLoading, onRowClick: handleRowClick, autoHeight: true, pageSizeOptions: [5, 10, 20], columns: [
                    {
                        field: 'Name',
                        headerName: $t('Name'),
                        width: 400,
                        renderCell: (cell) => {
                            const isFile = path.length !== 0;
                            const [icon, useTooltip] = (() => {
                                if (isFile) {
                                    return [FileIcon, false];
                                }
                                if (!isFile && cell.row.Valid) {
                                    return [FolderIcon, false];
                                }
                                return [WarningIcon, true];
                            })();
                            return (_jsxs(GridRow, { wrap: "nowrap", children: [_jsx(Tooltip, { title: useTooltip
                                            ? $t("A Data Interchange is configured to access a folder that isn't available")
                                            : '', children: _jsx(Icon, { iconUrl: icon, style: {
                                                color: useTooltip
                                                    ? theme.palette.warning.main
                                                    : 'initial'
                                            } }) }), isFile && (_jsx(Link, { role: "button", tabIndex: 0, onKeyDown: () => { }, onClick: () => {
                                            downloadSFTPFile(cell.value, path).then((response) => {
                                                const responseData = JSON.parse(response.Data);
                                                const fileData = responseData.Bytes;
                                                const fileName = responseData.Filename;
                                                downloadBase64File(fileName, fileData);
                                                const LogData = {
                                                    Key: fileName,
                                                    Type: 'SFTPDownload'
                                                };
                                                createLogEvent(LogData);
                                            });
                                        }, children: _jsx(Tooltip, { title: cell.value, children: _jsx(Typography, { style: { marginLeft: 8, cursor: 'pointer' }, noWrap: true, children: cell.value }) }) })), !isFile && (_jsx(Tooltip, { title: cell.value, children: _jsx(Typography, { style: { marginLeft: 8 }, noWrap: true, children: cell.value }) }))] }));
                        }
                    },
                    {
                        field: 'LastModifiedTime',
                        headerName: $t('Last Modified'),
                        sortComparator: (v1, v2) => {
                            const lastDate = new Date(v1);
                            const nextDate = new Date(v2);
                            return lastDate.getTime() - nextDate.getTime();
                        },
                        width: 400,
                        renderCell: (cell) => {
                            const isFile = path.length !== 0;
                            let lastModifiedStr = '-';
                            if (isFile) {
                                const lastModified = new Date(cell.row.LastModifiedTime);
                                lastModifiedStr = `${formatDate(lastModified, 'fullDateTime')} (${getTimezone(lastModified)})`;
                            }
                            return _jsx(Typography, { children: lastModifiedStr });
                        }
                    },
                    {
                        field: 'Size64',
                        headerName: $t('File Size'),
                        width: 200,
                        renderCell: (cell) => (_jsx(Typography, { children: cell.value
                                ? `${cell.value?.toLocaleString()} ${$t('bytes')}`
                                : '-' }))
                    },
                    {
                        field: 'UsedWith',
                        headerName: 'Used With',
                        width: 200,
                        renderCell: (cell) => _jsx(Typography, { children: cell.value || '-' })
                    },
                    {
                        field: 'ActionPlaceholder',
                        renderHeader: () => _jsx("span", {}),
                        width: 50,
                        renderCell: (cell) => cell.row.Type === 'file' ? (_jsx("div", { style: {
                                width: '100%',
                                textAlign: 'center'
                            }, children: _jsxs(IconMenu, { anchorHorizontalPosition: "left", onOpen: () => {
                                    setSelectedRow(cell.row);
                                }, children: [_jsx(MenuItem, { style: { width: '96px ' }, onClick: () => {
                                            setNewPath(path);
                                            setMoveModalOpen(true);
                                        }, children: $t('Move') }), !inArchive && !hasSFTPDownloadLog && (_jsx(MenuItem, { style: { width: '96px ' }, onClick: () => {
                                            setNewFilename(selectedRow.Name.split('.')
                                                .slice(0, -1)
                                                .join('.'));
                                            setRenameModalOpen(true);
                                            setNewFilename(selectedRow.Name.split('.')
                                                .slice(0, -1)
                                                .join('.'));
                                        }, children: $t('Rename') }))] }) })) : (_jsx("div", {}))
                    }
                ], rows: filteredData }), _jsx(Modal, { open: moveModalOpen, onClose: () => setMoveModalOpen(false), modalHeader: $t('Move File'), modalFooter: _jsxs(ModalFooter, { children: [_jsx(Button, { onClick: () => {
                                setMoveModalOpen(false);
                                moveSFTPFile(selectedRow.Filename, path, newPath).then(() => updateData());
                            }, children: $t('Move') }), _jsx(Button, { onClick: () => {
                                setMoveModalOpen(false);
                            }, children: $t('Close') })] }), children: selectedRow && (_jsx(GridRow, { style: { marginTop: 5 }, children: _jsx(Select, { id: "path-select", label: $t('Directory'), value: newPath, onChange: (e) => setNewPath(e.target.value), fullWidth: true, children: data
                            .filter((folder) => folder.Valid)
                            .map((folder) => (_jsx(MenuItem, { value: folder.Path, children: folder.Path }, folder.Path))) }) })) }), _jsx(Modal, { open: renameModalOpen, onClose: () => setRenameModalOpen(false), modalHeader: $t('Rename File'), modalFooter: _jsxs(ModalFooter, { children: [_jsx(Button, { onClick: () => {
                                setRenameModalOpen(false);
                                renameSFTPFile(selectedRow.Name, `${newFilename}.${selectedRow.Name.split('.').pop()}`, path).then(() => updateData());
                            }, children: $t('Rename') }), _jsx(Button, { onClick: () => {
                                setRenameModalOpen(false);
                            }, children: $t('Close') })] }), children: selectedRow && (_jsx(GridRow, { style: { marginTop: 5 }, children: _jsx(TextField, { label: $t('Filename'), fullWidth: true, value: newFilename, onChange: (e) => setNewFilename(e.target.value), endAdornment: `.${selectedRow.Name.split('.').pop()}` }) })) })] }));
}
export default FileTable;
